import React, { useState, useEffect, useCallback } from 'react';
import { FiAlertTriangle, FiPlus } from 'react-icons/fi';
import SectionTitle from '../templates/SectionTitle';
import TeamMemberCard from '../templates/TeamMemberCard';
import { btsApi } from '../services/btsApi';
import { btsShareHelper } from '../services/btsShareHelper';
import BtsTermsBox from '../templates/BtsTermsBox';
import { teamTerms } from '../data/mockData';
import './TeamPage.css';
const IS_PROD = process.env.REACT_APP_ENV === 'prod';
const log = (...args) => { if (!IS_PROD) console.log(...args); };

const MAX_MEMBERS = 4;
const LIFF_ID = process.env.REACT_APP_LIFF_ID;
const SHARE_BASE_URL = `https://liff.line.me/${LIFF_ID}`;

// =====================================================
// Validation: ชื่อตี้
// =====================================================
const REQUIRED_KEYWORDS = ['บิ๊กซี', 'BigC', 'BIGC', 'Bigc', 'Big C', 'BIG C'];

const BANNED_WORDS = [
  // คำหยาบไทย
  'เหี้ย', 'สัตว์', 'ควาย', 'หมา', 'ไอ้', 'แม่ง', 'เฮงซวย',
  'กระหรี่', 'กะหรี้', 'หี', 'ควย', 'จิ๋ม', 'เย็ด', 'แตด', 'สัส',
  'ส้นตีน', 'หำ', 'หำแตก', 'อีดอก', 'อีสัตว์', 'ไอ้สัตว์',
  'สถุล', 'ชาติหมา', 'ระยำ', 'ถ่อย', 'ชั่ว', 'งี่เง่า',
  'มึง', 'ตูด', 'ห่า', 'ตอแหล', 'ปัญญาอ่อน', 'บัดซบ',
  'อีช้าง', 'อีหน้าหมา', 'ไอ้หน้าหมา', 'กระสือ', 'ผีสาง',
  'เสือก', 'หน้าด้าน', 'หน้าทน', 'จัญไร', 'อัปรีย์',
  'โคตร', 'เชี่ย', 'แดก', 'สันดาน', 'ชิบหาย', 'เชือด',
  'อีตอ', 'อีแพศยา', 'นังร่าน', 'ร่าน', 'แม่สาว',
  'เสือก', 'อีโง่', 'ไอ้โง่', 'บ้าเอ๊ย', 'ไอ้บ้า', 'อีบ้า',
  'ไอ้เวร', 'อีเวร', 'สาระแน', 'ทุเรศ',
  'หน้าหี', 'หน้าควย', 'กะเทย','สัด',
  // คำหยาบอังกฤษ
  'fuck', 'shit', 'damn', 'bitch', 'dick', 'porn', 'sex', 'wtf',
  'ass', 'asshole', 'bastard', 'crap', 'idiot', 'stupid',
  'moron', 'retard', 'slut', 'whore', 'nigger', 'faggot',
  'cock', 'pussy', 'boob', 'nude', 'naked', 'hentai',
  // แบรนด์คู่แข่ง
  'โลตัส', 'lotus', 'tesco', 'makro', 'แม็คโคร', 'tops', 'ท็อปส์',
  '7-11', 'cjmore', 'เซเว่น', 'แฟมิลี่มาร์ท', 'familymart',
];

function validatePartyName(name) {
  const trimmed = name.trim();

  if (!trimmed) return 'กรุณาตั้งชื่อทีม';
  if (trimmed.length < 4) return 'ชื่อทีมต้องมีอย่างน้อย 4 ตัวอักษร';
  if (trimmed.length > 30) return 'ชื่อทีมต้องไม่เกิน 30 ตัวอักษร';

  // ต้องมีคำว่า บิ๊กซี/bigc
  const lower = trimmed.toLowerCase();
  const hasKeyword = REQUIRED_KEYWORDS.some((kw) => lower.includes(kw.toLowerCase()));
  if (!hasKeyword) return 'ชื่อทีมต้องมีคำว่า "บิ๊กซี", "Big C" หรือ "BigC" เป็นส่วนหนึ่งของชื่อ';

  // เช็คคำต้องห้าม
  const foundBanned = BANNED_WORDS.find((w) => lower.includes(w.toLowerCase()));
  if (foundBanned) return 'ชื่อทีมมีคำที่ไม่เหมาะสม กรุณาเปลี่ยน';

  return null; // ผ่าน
}

const TeamPage = ({ showPopup }) => {
  const [party, setParty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [partyName, setPartyName] = useState('');
  const [nameError, setNameError] = useState(null);

  const fetchParty = useCallback(async () => {
    try {
      log('[BTS Team] Fetching my party...');
      const res = await btsApi.getMyParty();
      log('[BTS Team] My party:', res);
      setParty(res?.data || null);
    } catch (err) {
      console.error('[BTS Team] Fetch party error:', err);
      setParty(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchParty();
  }, [fetchParty]);

  // validate ชื่อทุกครั้งที่พิมพ์
  const handleNameChange = (e) => {
    const val = e.target.value;
    setPartyName(val);
    if (val.trim()) {
      setNameError(validatePartyName(val));
    } else {
      setNameError(null);
    }
  };

  const handleCreateParty = async () => {
    const err = validatePartyName(partyName);
    if (err) {
      setNameError(err);
      return;
    }

    try {
      setCreating(true);
      log('[BTS Team] Creating party:', partyName.trim());
      const res = await btsApi.createParty(partyName.trim());
      log('[BTS Team] Party created:', res);
      sessionStorage.setItem('bts_just_joined', Date.now().toString());

      setShowCreateForm(false);
      setPartyName('');
      await fetchParty();

      // สร้างสำเร็จ → popup share เลย
      const newParty = (await btsApi.getMyParty())?.data;
      if (showPopup && newParty?.party_code) {
        const shareUrl = `${SHARE_BASE_URL}/game/bts?invite=${newParty.party_code}`;
        showPopup('สร้างทีมสำเร็จ!', `ทีม "${partyName.trim()}" พร้อมแล้ว!\nชวนเพื่อนให้ครบ 4 คน เพื่อลุ้นรางวัล`, 'success', [
          { label: 'ชวนเพื่อนเลย', primary: true, onClick: () => {
            btsShareHelper.smartShare(newParty.party_code, shareUrl);
          }},
          { label: 'ปิด', primary: false },
        ]);
      }
    } catch (err) {
      console.error('[BTS Team] Create party error:', err);
      const m = err?.message || '';
      const errorCode = err?.data?.error || '';
      const combined = `${errorCode} ${m}`;
      let title = 'สร้างทีมไม่สำเร็จ';
      let msg = 'กรุณาลองใหม่อีกครั้ง';

      // พนักงาน → block
      if (combined.includes('EMPLOYEE_BLOCKED')) {
        if (showPopup) {
          showPopup('ไม่สามารถร่วมกิจกรรมได้', 'พนักงานไม่สามารถร่วมเล่นเกมได้\nกรุณาติดตามกิจกรรมใหม่ในครั้งหน้า', 'error');
        }
        return;
      }

      // ไม่มี BigPoint → popup + redirect ไป register
      if (combined.includes('NO_BIGPOINT') || combined.includes('NO_BIGPOINT_ACCOUNT')) {
        if (showPopup) {
          showPopup(
            'ยังไม่ได้เป็นสมาชิก BigPoint',
            'คุณยังไม่มีบัญชี BigPoint\nต้องสมัครสมาชิก BigPoint ก่อนถึงจะสร้างทีมได้\n\nไปลงทะเบียนเลย!',
            'warning',
            [{ label: 'ลงทะเบียน BigPoint', primary: true, onClick: () => {
              window.location.href = '/register';
            }}]
          );
        }
        return;
      }

      if (combined.includes('PARTY_NAME_DUPLICATE'))   { title = 'ชื่อทีมซ้ำ'; msg = 'ชื่อทีมนี้มีคนใช้แล้ว กรุณาตั้งชื่อใหม่'; }
      else if (combined.includes('ALREADY_IN_PARTY'))      { title = 'มีทีมแล้ว'; msg = 'คุณอยู่ในทีมอื่นแล้ว ไม่สามารถสร้างทีมใหม่ได้\n(1 คน = 1 ทีม)'; }
      else if (combined.includes('CAMPAIGN_NOT_ACTIVE')) { title = 'นอกช่วงเวลา'; msg = 'ขณะนี้อยู่นอกช่วงเวลาแคมเปญ'; }
      else if (combined.includes('PLAYER_NOT_FOUND'))  { title = 'ไม่พบผู้เล่น'; msg = 'กรุณาเข้าสู่ระบบใหม่'; }
      else if (err?.isNetworkError)                    { title = 'ไม่มีสัญญาณ'; msg = 'ไม่สามารถเชื่อมต่อได้ กรุณาตรวจสอบอินเทอร์เน็ต'; }
      else {
        // ใช้ message จาก backend ถ้ามี
        title = err?.data?.title || title;
        msg = err?.data?.message || m || msg;
      }

      if (showPopup) {
        showPopup(title, msg, 'error');
      }
    } finally {
      setCreating(false);
    }
  };

  const handleShare = () => {
    if (!party?.party_code) return;
    const shareUrl = `${SHARE_BASE_URL}/game/bts?invite=${party.party_code}`;
    log('[BTS Team] Sharing invite link:', shareUrl);
    btsShareHelper.smartShare(party.party_code, shareUrl);
  };

  // ===== Loading =====
  if (loading) {
    return (
      <div>
        <SectionTitle icon="👥">ทีมของฉัน</SectionTitle>
        <div className="bts-team__loading">กำลังโหลด...</div>
      </div>
    );
  }

  // ===== ยังไม่มีทีม =====
  if (!party) {
    return (
      <div>
        <SectionTitle icon="👥">ทีมของฉัน</SectionTitle>

        {!showCreateForm ? (
          <div className="bts-team__empty">
            {/* <span className="bts-team__empty-big-icon">👥</span> */}
            <p className="bts-team__empty-title">คุณยังไม่มีทีม</p>
            <p className="bts-team__empty-desc">สร้างทีมเพื่อช่วยกันสะสมคะแนน!</p>
            <button
              className="bts-team__create-btn"
              onClick={() => setShowCreateForm(true)}
            >
              <FiPlus size={18} />
              สร้างทีมใหม่
            </button>
          </div>
        ) : (
          <div className="bts-team__create-form">
            <p className="bts-team__form-title">ตั้งชื่อทีม</p>
            <p className="bts-team__form-subtitle">
              ชื่อต้องมีคำว่า <strong>"บิ๊กซี"</strong> หรือ <strong>"BigC"</strong> รวมอยู่ด้วย
            </p>

            <div className="bts-team__input-wrap">
              <input
                className={`bts-team__input ${nameError ? 'bts-team__input--error' : partyName.trim() && !nameError ? 'bts-team__input--valid' : ''}`}
                type="text"
                placeholder='เช่น "บิ๊กซี ทีมแชมป์"'
                value={partyName}
                onChange={handleNameChange}
                maxLength={30}
              />
              <span className="bts-team__char-count">{partyName.length}/30</span>
            </div>

            {nameError && <p className="bts-team__input-error">{nameError}</p>}

            <div className="bts-team__form-actions">
              <button
                className="bts-team__form-submit"
                onClick={handleCreateParty}
                disabled={creating || !!nameError || !partyName.trim()}
              >
                {creating ? 'กำลังสร้าง...' : '🎉 สร้างทีมเลย!'}
              </button>
              <button
                className="bts-team__form-cancel"
                onClick={() => { setShowCreateForm(false); setPartyName(''); setNameError(null); }}
              >
                ยกเลิก
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ===== มีทีมแล้ว =====
  const members = party.members || [];
  const memberCount = members.length;
  const isTeamFull = memberCount >= MAX_MEMBERS;
  const emptySlots = MAX_MEMBERS - memberCount;

  const currentUserId = localStorage.getItem('userId');
  const mappedMembers = members.map((m) => ({
    id: m.id,
    name: m.display_name || 'สมาชิก',
    avatar: m.is_owner ? '👑' : '👤',
    pictureUrl: m.picture_url || null,
    points: Math.floor(m.personal_tickets || 0),
    isCurrentUser: m.line_id === currentUserId,
    isOwner: m.is_owner || false,
    isNewMember: m.is_new_member || false,
  }));

  return (
    <div>
      <SectionTitle icon="👥">ทีมของฉัน</SectionTitle>

      {isTeamFull ? (
        <div className="bts-team__success-badge">
          🎉 ทีมครบ {MAX_MEMBERS} คนแล้ว — พร้อมลุ้นรางวัล!
        </div>
      ) : (
        <div className="bts-team__warning">
          <FiAlertTriangle size={18} className="bts-team__warning-icon" />
          <div>
            <p className="bts-team__warning-title">ทีมยังไม่ครบ!</p>
            <p className="bts-team__warning-text">
              ต้องมีสมาชิกครบ {MAX_MEMBERS} คน ถึงจะมีสิทธิ์ลุ้นรางวัล
              <br />
              เหลืออีก <strong>{emptySlots} คน</strong> — ชวนเพื่อนเข้าร่วมเลย!
            </p>
          </div>
        </div>
      )}

      <div className="bts-team__card">
        <div className="bts-team__header">
          {/* <div className="bts-team__icon-box">🍗</div> */}
          <div>
            <p className="bts-team__name">ทีม {party.party_name || 'ของฉัน'}</p>
            {/* <p className="bts-team__meta">{memberCount} สมาชิก • อันดับ #{party.rank || '-'}</p> */}
          </div>
        </div>

        <div className="bts-team__grid">
          {mappedMembers.map((member) => (
            <TeamMemberCard key={member.id} member={member} isTeamFull={isTeamFull} />
          ))}
          {Array.from({ length: emptySlots }).map((_, i) => (
            <div key={`empty-${i}`} className="bts-team__empty-slot">
              {/* <span className="bts-team__empty-icon">+</span> */}
              <span className="bts-team__empty-text">เพิ่มเพื่อน</span>
            </div>
          ))}
        </div>

        <button
          className={`bts-team__invite-btn ${isTeamFull ? 'bts-team__invite-btn--disabled' : ''}`}
          onClick={isTeamFull ? undefined : handleShare}
          disabled={isTeamFull}
        >
          {isTeamFull ? 'ทีมครบแล้ว' : 'ชวนเพื่อนเข้าร่วม +'}
        </button>
      </div>

      <div className="bts-team__stats">
        <div className="bts-team__stat">
          <p className="bts-team__stat-label">รวมคะแนนทีม</p>
          <p className="bts-team__stat-value bts-team__stat-value--orange">
            {Math.floor(party.total_tickets || 0).toLocaleString()}
          </p>
        </div>
        <div className="bts-team__stat-divider" />
        <div className="bts-team__stat">
          <p className="bts-team__stat-label">สมาชิก</p>
          <p className="bts-team__stat-value">{memberCount}/{MAX_MEMBERS} คน</p>
        </div>
      </div>

      <BtsTermsBox title='เงื่อนไข : การตั้ง”ทีมของฉัน”' terms={teamTerms} />

      <div className="bts-team__spacer" />
    </div>
  );
};

export default TeamPage;
